import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import { canonicalizeDropInPaymentOptions } from "@bw/shared/utils";
import { useDropInPaymentOptions } from "../hooks/useDropInPaymentOptions";

/**
 * The way into "Check in" (#2691) from the drop-in page: a player with a pass
 * or membership can redeem one visit as a check-in code for the entrance
 * kiosk instead of checking in here. Shown only while drop-in is running and
 * the player holds a pass with visits left or a membership (the payment
 * options query is shared with the page, so this adds no request).
 */
export function KioskCodeEntry({ facilityId }: { facilityId: string }) {
  const optionsQ = useDropInPaymentOptions(facilityId);
  const options = optionsQ.data ? canonicalizeDropInPaymentOptions(optionsQ.data) : null;
  const redeemable =
    Boolean(options?.active_now) &&
    ((options?.active_packages ?? []).some((option) => option.remaining_visits > 0) ||
      (options?.active_memberships ?? []).length > 0);
  if (!redeemable) return null;
  return (
    <Paper
      variant="outlined"
      sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.25, mt: 3, borderRadius: "10px" }}
    >
      <QrCode2Rounded aria-hidden sx={{ color: "primary.main" }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
          At the entrance kiosk?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
          Redeem one visit from your pass as a check-in code.
        </Typography>
      </Box>
      <Button
        component={RouterLink}
        to={`/dropin/${facilityId}/check-in`}
        variant="outlined"
        size="small"
        sx={{ flex: "none", whiteSpace: "nowrap" }}
      >
        Show a code
      </Button>
    </Paper>
  );
}
